import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Lock } from "lucide-react";

const roleHome = { OWNER: "/app/dashboard", WAITRESS: "/app/pos", KITCHEN: "/app/kds", RECEPTION: "/app/pms" };

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(username, password);
      toast.success(`Добре дошли, ${u.full_name || u.username}`);
      nav(roleHome[u.role] || "/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1716697792926-202177e81980?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
             alt="Varbitsa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest-deep/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="gold-rule w-24 mb-6" />
          <h2 className="font-display text-5xl font-semibold leading-tight">Върбица</h2>
          <p className="text-cream/80 mt-3 max-w-md">Система за управление на хотел и ресторант — спокойствие сред природата.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-cream">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex justify-center mb-6"><LogoMark size={64} /></div>
          <h1 className="font-display text-3xl font-semibold text-forest-deep text-center">Вход за персонал</h1>
          <div className="gold-rule w-16 mx-auto my-4" />
          <p className="text-center text-muted-foreground text-sm mb-8">Влезте с потребителско име и парола</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-forest">Потребителско име</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold-dark" />
                <Input data-testid="login-username" value={username} autoCapitalize="none"
                       onChange={(e) => setUsername(e.target.value)} placeholder="напр. owner"
                       className="pl-10 h-12 bg-white" required />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-forest">Парола</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold-dark" />
                <Input data-testid="login-password" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                       className="pl-10 h-12 bg-white" required />
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
                    className="w-full h-12 bg-forest hover:bg-forest-light text-white rounded-xl text-base">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Влез"}
            </Button>
          </div>

          <Link to="/site" className="block text-center text-sm text-gold-dark hover:text-forest mt-6 transition-colors">
            ← Към публичния сайт
          </Link>
        </form>
      </div>
    </div>
  );
}
